import {
  ErrorList,
  Field,
  GeneralErrorBoundary,
  StatusButton,
} from "@/aptim/aptim";
import { getFormProps, getInputProps, useForm } from "@conform-to/react";
import { getZodConstraint, parseWithZod } from "@conform-to/zod";
import {
  json,
  redirect,
  type ActionFunctionArgs,
  type MetaFunction,
} from "@remix-run/node";
import { Form, Link, useActionData } from "@remix-run/react";
import { HoneypotInputs } from "remix-utils/honeypot/react";
import { z } from "zod";
import { ForgotPasswordEmail } from "~/components/emails/ForgotPassword.tsx";
import img from "~/img/undraw_forgot_password_re_hxwm.svg?url";
import { db } from "~/utils/db.server.ts";
import { sendEmail } from "~/utils/email.server.ts";
import { checkHoneypot } from "~/utils/honeypot.server.ts";
import { appDetails, useIsPending } from "~/utils/misc.tsx";
import { EmailSchema } from "~/utils/user-validation.ts";
import Layout from "./Layout.tsx";
import { prepareVerification } from "./verify.server.ts";

const ForgotPasswordSchema = z.object({
  usernameOrEmail: EmailSchema,
});

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  checkHoneypot(formData);
  const submission = await parseWithZod(formData, {
    schema: ForgotPasswordSchema.superRefine(async (data, ctx) => {
      const user = await db.user.findFirst({
        where: {
          OR: [{ email: data.usernameOrEmail }],
        },
        select: { id: true },
      });
      if (!user) {
        ctx.addIssue({
          path: ["usernameOrEmail"],
          code: z.ZodIssueCode.custom,
          message: "No user exists with this email",
        });
        return;
      }
    }),
    async: true,
  });
  if (submission.status !== "success") {
    return json(
      { result: submission.reply() },
      { status: submission.status === "error" ? 400 : 200 }
    );
  }
  const { usernameOrEmail } = submission.value;

  const user = await db.user.findFirstOrThrow({
    where: { OR: [{ email: usernameOrEmail }] },
    select: { email: true },
  });

  const { verifyUrl, redirectTo, otp } = await prepareVerification({
    period: 10 * 60,
    request,
    type: "reset-password",
    target: usernameOrEmail,
  });

  const response = await sendEmail({
    to: user.email,
    subject: `${appDetails.title} by ${appDetails.companyShortName} Password Reset`,
    react: (
      <ForgotPasswordEmail onboardingUrl={verifyUrl.toString()} otp={otp} />
    ),
  });

  if (response.status === "success") {
    return redirect(redirectTo.toString());
  } else {
    return json(
      { result: submission.reply({ formErrors: [response.error.message] }) },
      { status: 500 }
    );
  }
}

export const meta: MetaFunction = () => {
  return [{ title: `Password Recovery for ${appDetails.title}` }];
};

export default function ForgotPasswordPage() {
  const actionData = useActionData<typeof action>();
  const isPending = useIsPending();

  const [form, fields] = useForm({
    id: "forgot-password-form",
    constraint: getZodConstraint(ForgotPasswordSchema),
    lastResult: actionData?.result,
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: ForgotPasswordSchema });
    },
    shouldRevalidate: "onBlur",
  });

  return (
    <Layout
      title="Forgot Password!"
      subtitle={`${appDetails.title} by ${appDetails.companyShortName}`}
      img={img}
    >
      <div className="p-6">
        <Form method="POST" {...getFormProps(form)}>
          <HoneypotInputs />
          <div>
            <Field
              labelProps={{
                htmlFor: fields.usernameOrEmail.id,
                children: "Email",
              }}
              inputProps={{
                autoFocus: true,
                autoComplete: "off",
                ...getInputProps(fields.usernameOrEmail, {
                  type: "text",
                }),
              }}
              errors={fields.usernameOrEmail.errors}
            />
          </div>
          <ErrorList errors={form.errors} id={form.errorId} />

          <div className="mt-6">
            <StatusButton
              className="w-full"
              status={isPending ? "pending" : form.status ?? "idle"}
              type="submit"
              disabled={isPending}
            >
              Recover password
            </StatusButton>
          </div>
        </Form>
        <div className="flex items-center justify-center gap-2 pt-6">
          <span className="text-muted-foreground">
            Already have an account?
          </span>
          <Link to="/login">Login</Link>
        </div>
      </div>
    </Layout>
  );
}

export function ErrorBoundary() {
  return <GeneralErrorBoundary />;
}
